import React, { useState } from 'react';
import { RefreshCw, Settings, Gift, Clock, User, Tag } from 'lucide-react';
import { usePermission, useRole } from '../../../hooks/useAuth';
import { useTransactionLimitsConfig } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../ui/Loading';
import { Card, CardContent } from '../../ui/Card';
import Button from '../../ui/Button';
import AdminTabs from '../../ui/AdminTabs';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';
import ConfigForm from './ConfigForm';
import GrantsList from './GrantsList';
import { PageHeader } from '@kioskgaming/ui';
import { useFormatDate } from '../../../hooks/useFormatDate';

type TabType = 'config' | 'grants';

const SignupBonusManagement: React.FC = () => {
  // Reuse the transaction-limits permission — signup bonus config lives in the same block.
  const canManage = usePermission('transactionLimitsManagement');
  const canEdit = useRole(['system_admin', 'super_admin', 'finance_admin']);
  const fmt = useFormatDate();
  const { data: configData, isLoading, error, refetch } = useTransactionLimitsConfig();
  const [activeTab, setActiveTab] = useState<TabType>('config');

  if (!canManage) {
    return <AccessDeniedPanel message="You don&apos;t have permission to manage the welcome bonus." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-destructive text-lg font-semibold">Failed to load configuration</p>
          <p className="text-muted-foreground mt-2">
            {error instanceof Error ? error.message : 'An error occurred while loading the configuration.'}
          </p>
          <Button onClick={() => refetch()} className="mt-4" variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !configData) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  const config = configData.data;
  const updatedBy = typeof config.updatedBy === 'object' ? config.updatedBy : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Welcome Bonus"
        description="First-time player welcome bonus — real-time config, grants and promo cost"
        actions={
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Clock className="h-5 w-5 text-blue-600" /></div>
              <div>
                <p className="text-xs text-muted-foreground">Last Updated</p>
                <p className="text-sm font-medium">{config.updatedAt ? fmt(config.updatedAt) : 'Never'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg"><User className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-xs text-muted-foreground">Updated By</p>
                <p className="text-sm font-medium">{updatedBy ? updatedBy.name || updatedBy.email : 'System'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg"><Tag className="h-5 w-5 text-purple-600" /></div>
              <div>
                <p className="text-xs text-muted-foreground">Version</p>
                <p className="text-sm font-medium font-mono">{config.version}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <AdminTabs<TabType>
            variant="underline"
            navClassName="px-2 sm:px-4"
            tabs={[
              { id: 'config', label: 'Configuration', icon: Settings },
              { id: 'grants', label: 'Grants', icon: Gift },
            ]}
            value={activeTab}
            onChange={setActiveTab}
            aria-label="Welcome bonus tabs"
          />
        </CardContent>
      </Card>

      <div className="mt-6">
        {activeTab === 'config' && (
          <ConfigForm config={config} onSuccess={() => refetch()} canEdit={canEdit} />
        )}
        {activeTab === 'grants' && <GrantsList />}
      </div>
    </div>
  );
};

export default SignupBonusManagement;
